import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import BlogSidebar from './BlogSidebar';
import BlogPagination from './BlogPagination';
import './blog.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const categoryToSlug = (cat) =>
  (cat || '').toLowerCase().replace(/&/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const BlogList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [tag, setTag] = useState(searchParams.get('tag') || '');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const isFiltered = search || category || tag;

  useEffect(() => {
    fetchBlogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, category, tag]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 8, isActive: 'true' });
      if (search) params.set('search', search);
      if (category) params.set('category', category);
      if (tag) params.set('tag', tag);

      const res = await fetch(`${API_URL}/api/blogs?${params}`);
      const data = await res.json();
      if (data.success) {
        setBlogs(data.data);
        setTotal(data.pagination?.total || 0);
        setTotalPages(data.pagination?.pages || data.pagination?.totalPages || 1);
      }
    } catch (e) {
      console.error('Error fetching blogs:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
    const p = new URLSearchParams();
    if (searchInput.trim()) p.set('search', searchInput.trim());
    if (category) p.set('category', category);
    if (tag) p.set('tag', tag);
    setSearchParams(p);
  };

  const handleCategorySelect = (cat) => {
    const newCat = cat === category ? '' : cat;
    setCategory(newCat);
    setTag('');
    setPage(1);
    const p = new URLSearchParams();
    if (newCat) p.set('category', newCat);
    if (search) p.set('search', search);
    setSearchParams(p);
  };

  const handleTagSelect = (t) => {
    const newTag = t === tag ? '' : t;
    setTag(newTag);
    setPage(1);
    const p = new URLSearchParams();
    if (newTag) p.set('tag', newTag);
    if (search) p.set('search', search);
    setSearchParams(p);
  };

  const clearFilters = () => {
    setSearch(''); setSearchInput(''); setCategory(''); setTag(''); setPage(1);
    setSearchParams({});
  };

  const handlePageChange = (p) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClick = () => window.scrollTo(10, 0);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  const getImageUrl = (blog) =>
    blog.featuredImage?.fileId
      ? `${API_URL}/api/upload/file/${blog.featuredImage.fileId}`
      : null;

  const blogUrl = (blog) =>
    `/${blog.type || 'blog'}/${categoryToSlug(blog.category)}/${blog.slug}`;

  return (
    <>
      <Helmet>
        <title>Blog | BB Verma - CA Insights & Articles</title>
        <meta
          name="description"
          content="Expert articles on Income Tax, GST, Corporate Law, Audit & Assurance, Financial Planning and compliance by CA Binod Verma."
        />
        <meta
          name="keywords"
          content="income tax, GST, corporate law, audit, financial planning, compliance, TDS, chartered accountant, CA blog"
        />
        <link rel="canonical" href={window.location.href} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Blog | BB Verma - CA Insights & Articles" />
        <meta property="og:description" content="Expert articles on Income Tax, GST, Corporate Law and Financial Planning by CA Binod Verma." />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:site_name" content="BB Verma" />
      </Helmet>

      <section className="blog_section section_space bg-light">
        <div className="container">

          {/* ── MAIN SECTION: LIST + SIDEBAR ── */}
          <div className="row">
            {/* Blog List */}
            <div className="col-lg-8">

              {/* Search bar + filter status */}
              <div style={{ marginBottom: '28px' }}>
                <form onSubmit={handleSearch} style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#BFC9D1' }}></i>
                    <input
                      type="text"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      placeholder="Search articles..."
                      style={{ width: '100%', padding: '11px 14px 11px 40px', border: '2px solid #EAEFEF', borderRadius: '8px', fontSize: '14px', outline: 'none' }}
                    />
                  </div>
                  <button
                    type="submit"
                    className="btn btn-dark"
                    style={{ padding: '0 22px', whiteSpace: 'nowrap' }}
                  >
                    <span className="btn_label" data-text="Search">Search</span>
                  </button>
                </form>

                {isFiltered && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', fontSize: '13px', color: '#64748b' }}>
                    <span><strong>{total}</strong> {total === 1 ? 'result' : 'results'} found</span>
                    {category && (
                      <span style={{ background: '#FF9B51', color: '#fff', padding: '3px 10px', borderRadius: '20px' }}>
                        {category}
                        <button onClick={() => handleCategorySelect(category)} style={{ background: 'none', border: 'none', color: '#fff', marginLeft: '6px', cursor: 'pointer', padding: 0 }}>×</button>
                      </span>
                    )}
                    {tag && (
                      <span style={{ background: '#25343F', color: '#fff', padding: '3px 10px', borderRadius: '20px' }}>
                        #{tag}
                        <button onClick={() => handleTagSelect(tag)} style={{ background: 'none', border: 'none', color: '#fff', marginLeft: '6px', cursor: 'pointer', padding: 0 }}>×</button>
                      </span>
                    )}
                    <button onClick={clearFilters} style={{ background: 'none', border: '1px solid #EAEFEF', borderRadius: '6px', padding: '3px 10px', cursor: 'pointer', fontSize: '12px' }}>
                      Clear all
                    </button>
                  </div>
                )}
              </div>

              {/* Blog Cards */}
              {loading ? (
                <div>
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="blog_post_block image_left_layout" style={{ opacity: 0.5 }}>
                      <div className="blog_post_image" style={{ background: '#f0f0f0', minHeight: '160px' }}></div>
                      <div className="blog_post_content">
                        <div style={{ height: '14px', background: '#f0f0f0', borderRadius: '4px', marginBottom: '10px', width: '40%' }}></div>
                        <div style={{ height: '24px', background: '#f0f0f0', borderRadius: '4px', marginBottom: '10px' }}></div>
                        <div style={{ height: '14px', background: '#f0f0f0', borderRadius: '4px', width: '70%' }}></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : blogs.length > 0 ? (
                <>
                  {blogs.map((blog) => {
                    const img = getImageUrl(blog);
                    return (
                      <div className="blog_post_block image_left_layout" key={blog._id || blog.slug}>
                        <div className="blog_post_image">
                          <Link to={blogUrl(blog)} className="image_wrap" onClick={handleClick}>
                            {img ? (
                              <img src={img} alt={blog.title} style={{ width: '100%', height: '180px', objectFit: 'cover' }} />
                            ) : (
                              <div style={{ height: '180px', background: '#EAEFEF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#BFC9D1', fontSize: '36px' }}>
                                <i className="fa-regular fa-image"></i>
                              </div>
                            )}
                          </Link>
                        </div>
                        <div className="blog_post_content">
                          <div className="post_meta_wrap">
                            {blog.category && (
                              <ul className="category_btns_group unordered_list">
                                <li>
                                  <button
                                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit', font: 'inherit' }}
                                    onClick={() => handleCategorySelect(blog.category)}
                                  >
                                    {blog.category}
                                  </button>
                                </li>
                              </ul>
                            )}
                            <ul className="post_meta unordered_list">
                              <li>
                                <span>
                                  <i className="fa-regular fa-calendar-days" style={{ marginRight: '5px' }}></i>
                                  {formatDate(blog.publishedAt)}
                                </span>
                              </li>
                              <li>
                                <span>
                                  <i className="fa-regular fa-eye" style={{ marginRight: '5px' }}></i>
                                  {blog.viewCount || 0} views
                                </span>
                              </li>
                              {blog.commentCount > 0 && (
                                <li>
                                  <span>
                                    <i className="fa-regular fa-comments" style={{ marginRight: '5px' }}></i>
                                    {blog.commentCount}
                                  </span>
                                </li>
                              )}
                            </ul>
                          </div>
                          <h3 className="blog_post_title">
                            <Link to={blogUrl(blog)} onClick={handleClick}>{blog.title}</Link>
                          </h3>
                          {blog.excerpt && (
                            <p>
                              {blog.excerpt.length > 140 ? blog.excerpt.substring(0, 140) + '...' : blog.excerpt}
                            </p>
                          )}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '10px', flexWrap: 'wrap', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#64748b' }}>
                              <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#FF9B51', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '12px' }}>
                                {blog.authorName?.charAt(0).toUpperCase() || 'A'}
                              </div>
                              <span>{blog.authorName || 'Admin'}</span>
                            </div>
                            <Link to={blogUrl(blog)} onClick={handleClick} className="btn btn-dark" style={{ padding: '0 20px' }}>
                              <span className="btn_label" data-text="Read More">Read More</span>
                              <span className="btn_icon"><i className="fa-solid fa-arrow-up-right"></i></span>
                            </Link>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {totalPages > 1 && (
                    <BlogPagination
                      currentPage={page}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                    />
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: '52px', color: '#BFC9D1', marginBottom: '20px' }}>
                    <i className="fa-regular fa-newspaper"></i>
                  </div>
                  <h3 style={{ fontSize: '20px', marginBottom: '10px' }}>No Articles Found</h3>
                  <p style={{ color: '#64748b', marginBottom: '20px' }}>
                    {search
                      ? `No results for "${search}"`
                      : category
                        ? `No articles in "${category}"`
                        : 'No articles published yet.'}
                  </p>
                  {isFiltered && (
                    <button className="btn btn-dark" onClick={clearFilters} style={{ padding: '0 24px' }}>
                      <span className="btn_label" data-text="Clear Filters">Clear Filters</span>
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="col-lg-4">
              <BlogSidebar
                onCategorySelect={handleCategorySelect}
                onTagSelect={handleTagSelect}
                currentCategory={category}
                currentTag={tag}
              />
            </div>
          </div>

        </div>
      </section>
    </>
  );
};

export default BlogList;
